import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { makeStyles } from 'tss-react/mui';
import { Button } from '@ui/buttons';
import { SidePanel } from '@ui/layout';
import {
  KalkylfilColumnMap,
  NODON_FIELD_LABEL,
  NodonField,
  buildHeaderLabels,
  headerKey,
  sortColumnsByNodonPriority,
} from '@shared/helpers/kalkylfil-csv.parser';
import { ProjectElementImport } from '@shared/models/element-import.model';
import { getDiscardTriggerByRowId } from '@shared/helpers/candidate-discard.helpers';
import { useSelectedVersion } from '@client/store/ui';
import { useDeleteElementImport } from './use-delete-element-import.hook';
import { useElementImports } from './use-element-imports.hook';
import {
  CandidateRowStatus,
  useImportedElements,
  computeImportProgressFromStatusCounts,
  resolveCandidateStatus,
} from './candidate-status.helpers';
import { useProductsLookup } from '@client/store/product';
import { useRecipeLookup } from '@client/store/recipe/recipe.hook';
import { formatImportCreatedAt } from './import-display.helpers';
import { buildDraftMappingsByRowId } from '@shared/helpers/import-row-draft.helpers';
import { ImportStatusChart } from './ImportStatusChart';
import { SidePanelHeader } from './SidePanelHeader';

const useStyles = makeStyles()((theme) => ({
  sectionLabel: {
    display: 'block',
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  detectionList: {
    display: 'flex',
    flexDirection: 'column',
  },
  detectionRow: {
    display: 'grid',
    gridTemplateColumns: '88px 1fr',
    alignItems: 'baseline',
    gap: theme.spacing(1.5),
    padding: theme.spacing(0.75, 0),
    borderTop: '1px solid rgba(0,0,0,0.06)',
  },
  roleLabel: {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: theme.palette.primary.main,
  },
  roleLabelUnmapped: {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: 'rgba(0,0,0,0.3)',
  },
  headerName: {
    fontSize: 13,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  headerNameUnmapped: {
    fontSize: 13,
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  interpretationValue: { fontSize: 13 },
  interpretationHint: {
    display: 'block',
    fontSize: 11,
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.25),
  },
}));

const WASTE_SCALE_LABEL = '1–2';
const WASTE_SCALE_HINT = '1 = 0% → 2 = 100%';

type DetectionEntry = {
  /** Tag.Header column key — stable React key. */
  key: string;
  /** Original CSV header, for display. */
  label: string;
  field: NodonField | undefined;
};

const buildDetectionEntries = (
  headers: readonly string[],
  columnMap: KalkylfilColumnMap,
  labels: Record<string, string>,
): DetectionEntry[] => {
  const headerToField = new Map<string, NodonField>();
  (Object.entries(columnMap) as [NodonField, string | undefined][]).forEach(
    ([field, header]) => {
      if (header) headerToField.set(headerKey(header), field);
    },
  );
  return sortColumnsByNodonPriority(headers, columnMap).map((header) => ({
    key: header,
    label: labels[header] ?? header,
    field: headerToField.get(headerKey(header)),
  }));
};

/** Side-panel content for a selected CSV import (group/header row). */
export const ImportSidePanel: React.FC<{
  imp: ProjectElementImport;
  projectId: number;
}> = ({ imp, projectId }) => {
  const { classes, cx } = useStyles();
  const version = useSelectedVersion();
  const importedElements = useImportedElements(version);
  const productsById = useProductsLookup();
  const recipesById = useRecipeLookup();
  const imports = useElementImports(projectId);
  const candidateRowIds = useMemo(() => {
    if (imports.status !== 'ready') return [];
    return (imports.data.rowsByImport[imp.id] ?? []).map((row) => row.id);
  }, [imports, imp.id]);
  const handleDelete = useDeleteElementImport(imp.id, candidateRowIds);

  const columnMap = imp.columnMap;
  const headerLabels = useMemo(
    () => buildHeaderLabels(imp.metadata?.catalog ?? []),
    [imp.metadata],
  );
  const detection = useMemo(
    () => buildDetectionEntries(imp.headers, columnMap, headerLabels),
    [imp.headers, columnMap, headerLabels],
  );
  const wasteScale = imp.metadata?.interpretations?.wasteScale;

  const counts = useMemo(() => {
    if (imports.status !== 'ready') return {};
    const rows = imports.data.rowsByImport[imp.id] ?? [];
    const mappingsByCode = imports.data.mappingsByCode;
    const discardTriggerByRowId = getDiscardTriggerByRowId(version);
    const importsById = { [imp.id]: imp };
    const draftMappingsByRowId = buildDraftMappingsByRowId(version, rows);
    const out: Partial<Record<CandidateRowStatus, number>> = {};
    for (const row of rows) {
      const s = resolveCandidateStatus(
        row,
        mappingsByCode,
        discardTriggerByRowId,
        productsById,
        importedElements,
        { importsById, draftMappingsByRowId, recipesById },
      );
      out[s] = (out[s] ?? 0) + 1;
    }
    return out;
  }, [imports, imp, version, productsById, recipesById, importedElements]);

  const importProgress = useMemo(() => {
    if (imports.status !== 'ready') {
      return {
        materialized: 0,
        discarded: 0,
        resolved: 0,
        total: 0,
        percent: 0,
      };
    }
    const rows = imports.data.rowsByImport[imp.id] ?? [];
    return computeImportProgressFromStatusCounts(counts, rows.length);
  }, [imports, imp.id, counts]);

  if (imports.status !== 'ready') return null;

  return (
    <SidePanel.Content>
      <SidePanel.Content.Section>
        <SidePanelHeader
          icon={
            <InsertDriveFileOutlinedIcon
              sx={{ fontSize: 18, color: 'rgba(0,0,0,0.7)' }}
            />
          }
          title={imp.fileName ?? 'Import'}
          subtitle={formatImportCreatedAt(imp)}
        />
      </SidePanel.Content.Section>
      <SidePanel.Content.Divider />
      <SidePanel.Content.Section>
        <span className={classes.sectionLabel}>Import status</span>
        <ImportStatusChart counts={counts} progress={importProgress} />
      </SidePanel.Content.Section>
      <SidePanel.Content.Divider />
      <SidePanel.Content.Section>
        <span className={classes.sectionLabel}>Column detection</span>
        <Box className={classes.detectionList}>
          {detection.map(({ key, label, field }) => (
            <Box key={key} className={classes.detectionRow}>
              <span
                className={cx({
                  [classes.roleLabel]: !!field,
                  [classes.roleLabelUnmapped]: !field,
                })}
              >
                {field ? NODON_FIELD_LABEL[field] : 'Unused'}
              </span>
              <span
                className={cx({
                  [classes.headerName]: !!field,
                  [classes.headerNameUnmapped]: !field,
                })}
                title={label}
              >
                {label}
              </span>
            </Box>
          ))}
        </Box>
      </SidePanel.Content.Section>
      <SidePanel.Content.Divider />
      {wasteScale && (
        <>
          <SidePanel.Content.Section>
            <span className={classes.sectionLabel}>Interpretations</span>
            <Box className={classes.detectionList}>
              <Box className={classes.detectionRow}>
                <span className={classes.roleLabel}>Spill scale</span>
                <Box>
                  <span className={classes.interpretationValue}>
                    {WASTE_SCALE_LABEL}
                  </span>
                  <span className={classes.interpretationHint}>
                    {WASTE_SCALE_HINT}
                  </span>
                </Box>
              </Box>
            </Box>
          </SidePanel.Content.Section>
          <SidePanel.Content.Divider />
        </>
      )}
      <SidePanel.Content.Section>
        <Button
          variant="outlined"
          color="inherit"
          onClick={() => void handleDelete()}
        >
          Delete import
        </Button>
      </SidePanel.Content.Section>
    </SidePanel.Content>
  );
};
